<template>
	<div class="pc-question-scroll-row">
		<button
			v-if="canScrollLeft"
			type="button"
			class="pc-question-scroll-row__btn pc-question-scroll-row__btn--left"
			:aria-label="t('向左滚动')"
			@click="scrollBy(-SCROLL_STEP)"
		>
			<img
				:src="scrollChevronRight"
				class="pc-question-scroll-row__icon pc-question-scroll-row__icon--left"
				width="32"
				height="32"
				alt=""
			/>
		</button>
		<div ref="trackRef" class="pc-question-scroll-row__track" @scroll="updateScrollState">
			<div class="pc-question-scroll-row__content" :class="contentClass">
				<slot />
			</div>
		</div>
		<button
			v-if="canScrollRight"
			type="button"
			class="pc-question-scroll-row__btn pc-question-scroll-row__btn--right"
			:aria-label="t('向右滚动')"
			@click="scrollBy(SCROLL_STEP)"
		>
			<img
				:src="scrollChevronRight"
				class="pc-question-scroll-row__icon"
				width="32"
				height="32"
				alt=""
			/>
		</button>
	</div>
</template>

<script setup lang="ts">
import { scrollChevronRight } from "@variants/seer/master-s/shared/images";
import { t } from "@variants/seer/master-s/shared/utils/i18n";

const SCROLL_STEP = 300;

withDefaults(
	defineProps<{
		contentClass?: string;
	}>(),
	{ contentClass: "" }
);

const trackRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

function updateScrollState() {
	const el = trackRef.value;
	if (!el) {
		canScrollLeft.value = false;
		canScrollRight.value = false;
		return;
	}

	const { scrollLeft, scrollWidth, clientWidth } = el;
	canScrollLeft.value = scrollLeft > 1;
	canScrollRight.value = scrollLeft + clientWidth < scrollWidth - 1;
}

function scrollBy(delta: number) {
	trackRef.value?.scrollBy({ left: delta, behavior: "smooth" });
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
	nextTick(updateScrollState);

	if (!import.meta.client) return;

	const el = trackRef.value;
	if (!el) return;

	resizeObserver = new ResizeObserver(() => updateScrollState());
	resizeObserver.observe(el);
	resizeObserver.observe(el.firstElementChild || el);
	window.addEventListener("resize", updateScrollState);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	resizeObserver = null;
	if (import.meta.client) {
		window.removeEventListener("resize", updateScrollState);
	}
});

defineExpose({ updateScrollState });
</script>

<style scoped>
/* Figma PC 常见问题滚动行 #1426:44128: row, alignSelf stretch, gap 8px, height 32px */
.pc-question-scroll-row {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-width: 0;
	height: 32px;
}

.pc-question-scroll-row__btn {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
}

.pc-question-scroll-row__btn:hover {
	opacity: 0.85;
}

.pc-question-scroll-row__icon {
	display: block;
	width: 32px;
	height: 32px;
	object-fit: contain;
}

.pc-question-scroll-row__icon--left {
	transform: scaleX(-1);
}

.pc-question-scroll-row__track {
	flex: 1 1 0;
	min-width: 0;
	height: 32px;
	overflow-x: auto;
	overflow-y: hidden;
	scrollbar-width: none;
	-ms-overflow-style: none;
}

.pc-question-scroll-row__track::-webkit-scrollbar {
	display: none;
}

.pc-question-scroll-row__content {
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	gap: 8px;
	width: max-content;
	min-height: 32px;
}
</style>
